// Guide data hooks — fetches Pillar Guides from the FastAPI reader.
// Mirrors the shape / behaviour of `blogApi.js` so consumers can treat
// a Guide document like a Blog Post for rendering purposes.
import { useEffect, useState } from "react";

const BASE = "/api/guides";

function _rewriteMediaUrl(u) {
  if (!u || typeof u !== "string") return u;
  const m = u.match(/^https?:\/\/[^/]+(\/api\/cms\/.+)$/i);
  if (m) return m[1];
  return u;
}

function _walkAndRewriteMedia(node) {
  if (node == null || typeof node !== "object") return node;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i += 1) node[i] = _walkAndRewriteMedia(node[i]);
    return node;
  }
  if (typeof node.url === "string" && (node.mimeType || node.filename || node.sizes)) {
    node.url = _rewriteMediaUrl(node.url);
    if (node.thumbnailURL) node.thumbnailURL = _rewriteMediaUrl(node.thumbnailURL);
    if (node.sizes && typeof node.sizes === "object") {
      for (const k of Object.keys(node.sizes)) {
        const s = node.sizes[k];
        if (s && typeof s === "object" && typeof s.url === "string") {
          s.url = _rewriteMediaUrl(s.url);
        }
      }
    }
  }
  for (const k of Object.keys(node)) {
    node[k] = _walkAndRewriteMedia(node[k]);
  }
  return node;
}

export async function fetchGuideBySlug(slug) {
  const r = await fetch(`${BASE}/${encodeURIComponent(slug)}?depth=1`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Guide failed: HTTP ${r.status}`);
  return _walkAndRewriteMedia(await r.json());
}

/** Map of service slug → guide slug, used by the Services page to
 *  link each service card to its dedicated Guide. Returns `{}` on
 *  network failure so the UI keeps working. */
export async function fetchGuideSlugMap() {
  try {
    const r = await fetch(`${BASE}?limit=100&depth=1`);
    if (!r.ok) return {};
    const body = await r.json();
    const map = {};
    for (const g of (body?.docs || [])) {
      const svc = g?.service;
      const svcSlug = typeof svc === "object" && svc ? svc.slug : null;
      if (svcSlug && g?.slug) map[svcSlug] = g.slug;
    }
    return map;
  } catch {
    return {};
  }
}

export function useGuideArticle(slug) {
  const [state, setState] = useState({ loading: true, error: null, notFound: false, data: null });
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ loading: true, error: null, notFound: false, data: null });

    // Fetch with one automatic retry on transient errors (network drop, 5xx,
    // timeout). A confirmed 404 does NOT retry. Prevents prerenderers /
    // crawler-caches from snapshotting a transient Not Found state and
    // caching `noindex` for the bot audience.
    const attempt = (retriesLeft) =>
      fetchGuideBySlug(slug)
        .then((data) => {
          if (cancelled) return;
          if (data === null) {
            setState({ loading: false, error: null, notFound: true, data: null });
          } else {
            setState({ loading: false, error: null, notFound: false, data });
          }
        })
        .catch((e) => {
          if (cancelled) return;
          if (retriesLeft > 0) {
            setTimeout(() => attempt(retriesLeft - 1), 500);
            return;
          }
          setState({ loading: false, error: e.message, notFound: false, data: null });
        });

    attempt(1);
    return () => { cancelled = true; };
  }, [slug]);
  return state;
}

export async function fetchGuideRelatedArticles(guideId, { limit = 4 } = {}) {
  try {
    const r = await fetch(`${BASE}/${encodeURIComponent(guideId)}/related-articles?limit=${limit}`);
    if (!r.ok) return [];
    const body = await r.json();
    return body?.docs || [];
  } catch {
    return [];
  }
}

export async function fetchGuideRelatedServices(guideId, { limit = 3 } = {}) {
  try {
    const r = await fetch(`${BASE}/${encodeURIComponent(guideId)}/related-services?limit=${limit}`);
    if (!r.ok) return [];
    const body = await r.json();
    return body?.docs || [];
  } catch {
    return [];
  }
}
